import React, { useMemo, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { GoogleAdsCampaignConfigItem, deleteGoogleAdsCampaignConfig } from '~/services/PublicOfferFeedService';

import dateFormatter from '~/utils/dateFormatter';

import GoogleAdsCampaignsConfigForm from './GoogleAdsCampaignsConfigForm';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 0.5,
};

const getColumns = ({
  onEdit,
  onDelete,
}: {
  onEdit: (row: GoogleAdsCampaignConfigItem) => void;
  onDelete: (row: GoogleAdsCampaignConfigItem) => void;
}): Array<GridColDef> => [
  { field: 'customer_id', headerName: 'Customer ID', ...sharedOpts },
  { field: 'campaign_id', headerName: 'Campaign ID', ...sharedOpts },
  { field: 'campaign_type', headerName: 'Type', ...sharedOpts },
  { field: 'enabled', headerName: 'Enabled', type: 'boolean', ...sharedOpts },
  {
    field: 'created_at',
    headerName: 'Created At',
    ...sharedOpts,
    renderCell: (params) => <Typography>{dateFormatter(params.row.created_at)}</Typography>,
  },
  {
    field: 'updated_at',
    headerName: 'Updated At',
    ...sharedOpts,
    renderCell: (params) => <Typography>{dateFormatter(params.row.updated_at)}</Typography>,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    ...sharedOpts,
    renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" size="small" onClick={() => onEdit(params.row)}>
          Edit
        </Button>
        <Button variant="outlined" size="small" onClick={() => onDelete(params.row)}>
          Delete
        </Button>
      </Stack>
    ),
  },
];

type Props = {
  customerId: string;
  setCustomerId: (value: string) => void;
  campaignId: string;
  setCampaignId: (value: string) => void;
  campaignType: 'offer' | 'search' | undefined;
  setCampaignType: (value: 'offer' | 'search' | undefined) => void;
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  data: Array<GoogleAdsCampaignConfigItem>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  fetchData: () => void;
};

export default function GoogleAdsCampaignsConfigTab({
  customerId,
  setCustomerId,
  campaignId,
  setCampaignId,
  campaignType,
  setCampaignType,
  enabled,
  setEnabled,
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  fetchData,
}: Props) {
  const [open, setOpen] = useState(false);
  const [editableRow, setEditableRow] = useState<GoogleAdsCampaignConfigItem | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<GoogleAdsCampaignConfigItem | undefined>(undefined);

  const rows = useMemo(() => data.map((row, index) => ({ id: index, ...row })), [data]);

  const handleOpen = () => {
    setEditableRow(undefined);
    setOpen(true);
  };

  const handleEdit = (row: GoogleAdsCampaignConfigItem) => {
    setEditableRow(row);
    setOpen(true);
  };

  const handleDelete = (row: GoogleAdsCampaignConfigItem) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (rowToDelete) {
      deleteGoogleAdsCampaignConfig(rowToDelete.campaign_id).then(() => {
        fetchData();
        setDeleteDialogOpen(false);
        setRowToDelete(undefined);
      });
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(undefined);
  };

  return (
    <>
      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid item xs={8} container spacing={2}>
          <Grid item xs={3}>
            <TextField
              id="customer_id"
              label="Customer ID"
              variant="outlined"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              id="campaign_id"
              label="Campaign ID"
              variant="outlined"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={3}>
            <Select
              id="campaign_type"
              displayEmpty
              value={campaignType ?? ''}
              onChange={(e) => setCampaignType((e.target.value || undefined) as 'offer' | 'search')}
              fullWidth
            >
              <MenuItem value="">Campaign Types</MenuItem>
              <MenuItem value="offer">Offer</MenuItem>
              <MenuItem value="search">Search</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  name="enabled"
                  color="primary"
                />
              }
              label="Enabled Campaigns only"
            />
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleOpen}>
              New Campaign Config
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <GoogleAdsCampaignsConfigForm open={open} setOpen={setOpen} fetchData={fetchData} editableRow={editableRow} />

      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the Google Ads campaign configuration for Campaign ID:{' '}
            {rowToDelete?.campaign_id}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} variant="text">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={rows}
          columns={getColumns({ onEdit: handleEdit, onDelete: handleDelete })}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
        />
      </Box>
    </>
  );
}
